import { getIn } from 'formik';
import React from 'react';
import * as yup from 'yup';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';

import { FormContext } from './FormContext';

/**
 * @typedef {object} FormGroupProps
 * @property {string} name - REQUIRED - O nome/chave para este grupo dentro do objeto de valores do formulário
 * @property {string} [label] - Um rótulo opcional exibido acima do grupo. (Opcional)
 * @property {any[]} [initialValue] - O array inicial de valores para o grupo. (Opcional)
 * @property {number} [minRows] - O número mínimo de linhas permitidas no grupo. (Opcional)
 * @property {number} [maxRows] - O número máximo de linhas permitidas no grupo. (Opcional)
 * @property {boolean} [disableAddButton] - Se verdadeiro, oculta o botão para adicionar novas linhas. (Opcional)
 * @property {boolean} [disableDeleteButton] - Se verdadeiro, oculta o botão para excluir linhas. (Opcional)
 * @property {number} [itemGridSize=12] - O valor da propriedade `xs` do Grid MUI para cada contêiner de item de linha. (Opcional, padrão: 12)
 * @property {number} [itemGridSpacing=1] - O valor da propriedade `spacing` do Grid MUI para o contêiner dos itens de cada linha. (Opcional, padrão: 1)
 * @property {import('yup').AnySchema} [validation] - Um esquema de validação Yup especificamente para o próprio array (ex: `yup.array().min(1)`). A validação para itens dentro do array é tratada pelos componentes filhos. (Opcional)
 * @property {function(currentValue: any[]): void} [changeListener] - Uma função de callback acionada quando o número de linhas no grupo muda. Recebe o valor atual do array. (Opcional)
 * @property {function(value: any): any} [serializeFunc] - Uma função para serializar o valor do grupo antes do envio ou salvamento no localStorage. (Opcional)
 * @property {function(value: any): any} [deserializeFunc] - Uma função para desserializar o valor do grupo ao carregar do localStorage ou valores iniciais. (Opcional)
 */

export class FormGroup extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      validationSchemaChildren: {},
      prevContextValue: null,
      removedRows: [],
    };
  }
  buildRow = (index) => {
    const shouldShowDeleteButton =
      !this.props.disableDeleteButton &&
      (!this.props.minRows || index >= this.props.minRows);
    return (
      <Grid
        key={this.translateToFullIndex(index)}
        item
        xs={this.props.itemGridSize ?? 12}
      >
        <Grid
          container
          spacing={this.props.itemGridSpacing ?? 1}
          sx={{ width: '100%' }}
        >
          <FormContext.Provider value={this.getFormContext(index)}>
            {this.props.children}
          </FormContext.Provider>
          <Grid item container xs='auto' alignItems='flex-start'>
            {shouldShowDeleteButton ? (
              <IconButton
                sx={{ p: 0, mt: '22px' }}
                size='small'
                onClick={() => this.removeRow(index)}
              >
                <DeleteIcon />
              </IconButton>
            ) : (
              !this.props.disableDeleteButton && <Grid sx={{ px: '12px' }} />
            )}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  translateToFullIndex = (index) => {
    const removedRows = this.state.removedRows;
    for (const removedIndex of removedRows) {
      if (removedIndex <= index) index = index + 1;
    }
    return index;
  };

  removeRow = (index) => {
    const value = getIn(this.context.values, this.props.name);
    if (index == null) index = value.length - 1;

    let valueFiltered = value.filter((_, i) => i != index);

    if (this.isEmpty()) valueFiltered = [...valueFiltered, {}];

    this.context.setFieldValue(this.props.name, valueFiltered);
    this.setState({
      removedRows: [
        ...this.state.removedRows,
        this.translateToFullIndex(index),
      ],
    });
  };

  addRow = () => {
    if (this.isFull()) return;

    let newValues;
    if (!this.context.values?.[this.props.name]) newValues = [{}];
    else newValues = [...this.context.values[this.props.name], {}];
    this.context.setFieldValue(this.props.name, newValues);
  };

  isFull = () => {
    return (
      this.props.maxRows &&
      getIn(this.context.values, this.props.name)?.length >= this.props.maxRows
    );
  };

  isFilled = () => {
    return getIn(this.context.values, this.props.name).every(
      (elem) => JSON.stringify(elem) != '{}',
    );
  };

  isEmpty = () => {
    return (
      this.props.minRows &&
      getIn(this.context.values, this.props.name)?.length <= this.props.minRows
    );
  };

  getFormContext = (index) => {
    const prefix = `${this.props.name}[${index}]`;
    const toGroupFunc = (formFunction) => {
      return (name, value) => {
        formFunction(`${prefix}.${name}`, value);
      };
    };

    return {
      formGroupIndex: index,
      values: getIn(this.context.values, this.props.name)?.[index],
      errors: getIn(this.context.errors, this.props.name)?.[index],
      touched: getIn(this.context.touched, this.props.name)?.[index],
      initialValues: getIn(this.context.initialValues, this.props.name)?.[
        this.translateToFullIndex(index)
      ],
      validate: this.context.validate,
      isSubmitting: this.context.isSubmitting,
      isValid: this.context.isValid,
      setFieldValue: toGroupFunc(this.context.setFieldValue),
      setFieldTouched: toGroupFunc(this.context.setFieldTouched),
      setCustomError: toGroupFunc(this.context.setCustomError),
      setInitialValue: toGroupFunc(this.context.setInitialValue),
      setSerializeFunc: toGroupFunc(this.context.setSerializeFunc),
      setDeserializeFunc: toGroupFunc(this.context.setDeserializeFunc),
      setFieldStepMap: toGroupFunc(this.context.setFieldStepMap),
      setCallbackFunc: (listenerField, dependentField, callbackFunc) => {
        if (dependentField.startsWith('thisGroup.'))
          dependentField = prefix + dependentField.slice('thisGroup.'.length);
        this.context.setCallbackFunc(
          `${prefix}.${listenerField}`,
          dependentField,
          callbackFunc,
        );
      },
      setValidationSchema: (name, schema) => {
        this.setState(
          (state) => {
            if (schema == null) {
              delete state.validationSchemaChildren[name];
            }
            state.validationSchemaChildren[name] = schema;
            return state;
          },
          () => {
            this.context.setValidationSchema(
              this.props.name,
              this.props.validation
                ? this.props.validation.of(
                    yup.object().shape(this.state.validationSchemaChildren),
                  )
                : yup
                    .array()
                    .of(
                      yup.object().shape(this.state.validationSchemaChildren),
                    ),
            );
          },
        );
      },
    };
  };

  getError = () => {
    return (
      Boolean(this.context.touched[this.props.name]) &&
      typeof getIn(this.context.errors, this.props.name) == 'string'
    );
  };

  getHelperText = () => {
    return this.getError() ? getIn(this.context.errors, this.props.name) : '';
  };

  componentDidMount() {
    this.updateInitialValue();
    this.updateChangeListener();
    if (this.props.serializeFunc)
      this.context.setSerializeFunc(this.props.name, this.props.serializeFunc);
    if (this.props.deserializeFunc)
      this.context.setDeserializeFunc(
        this.props.name,
        this.props.deserializeFunc,
      );
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(this.props.initialValue) !==
      JSON.stringify(prevProps.initialValue)
    ) {
      this.updateInitialValue();
    }
    this.updateChangeListener();
  }

  updateInitialValue = () => {
    let values;
    if (!this.props.initialValue || !this.props.initialValue.length)
      values = [{}];
    else values = this.props.initialValue;

    if (values.length < this.props.minRows)
      values = [
        ...values,
        ...Array(this.props.minRows - values.length).fill({}),
      ];
    if (values.length > this.props.maxRows)
      values = values.slice(0, this.props.maxRows);

    this.context.setInitialValue(this.props.name, values);
    this.context.setFieldValue(this.props.name, values);
  };

  updateChangeListener = () => {
    if (
      this.props.changeListener &&
      getIn(this.context.values, this.props.name)?.length !=
        this.state.prevContextValue?.length
    )
      this.props.changeListener(getIn(this.context.values, this.props.name));
    if (
      JSON.stringify(getIn(this.context.values, this.props.name)) !==
      JSON.stringify(this.state.prevContextValue)
    )
      this.setState({
        prevContextValue: getIn(this.context.values, this.props.name),
      });
  };

  // Isso é necessário para garantir que a validação rode depois que um
  // componente é desmontado. Um pouco gambiarra (devido ao strict mode
  // também), mas foi o que deu pra fazer.
  componentWillUnmount() {
    this.context.setInitialValue(this.props.name, undefined);
    this.context.setValidationSchema(this.props.name, undefined);
    this.context.setFieldValue(this.props.name, undefined);
    setTimeout(() => this.context.validate(), 300);
  }

  render() {
    let inputs;
    const values = getIn(this.context.values, this.props.name);
    const currentGroupSize = values?.length ?? 0;
    if (currentGroupSize) {
      inputs = values.map((_elem, index) =>
        this.buildRow(index, currentGroupSize),
      );
    }
    const shouldShowAddButton = !this.props.disableAddButton && !this.isFull();

    return (
      <>
        {this.props.label ? (
          <FormLabel sx={{ mb: '5px' }}>{this.props.label}</FormLabel>
        ) : null}
        <Grid container>{inputs}</Grid>
        <Grid container>
          <Grid item xs>
            {this.getError() && (
              <FormHelperText error>{this.getHelperText()}</FormHelperText>
            )}
          </Grid>
          {shouldShowAddButton ? (
            <Grid item xs='auto'>
              <IconButton
                sx={{ p: 0, mt: '10px' }}
                size='small'
                onClick={() => this.addRow()}
              >
                <AddIcon />
              </IconButton>
            </Grid>
          ) : null}
        </Grid>
      </>
    );
  }
}
FormGroup.contextType = FormContext;
